import { Key, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getFacility } from 'app/state/ducks/facilities/selectors'
import { Table, Text } from '@mantine/core'
import { amortizationsStatus, frequencyOptions } from 'app/models/dropdown-options'
import { setOneFacility } from 'app/state/ducks/facilities/thunks'
import {
  SchedulerList
} from 'app/models/amortitation-schedule'
import { getFacilityAmendments } from 'app/state/ducks/facility-amendments/selectors'
import { getLoan } from 'app/state/ducks/loans/selectors'
import { calculateInterestPaymentScheduler, formatDateToShow, exportToCSV } from 'app/utils/util-functions'
import { getBusinessDate } from 'app/state/ducks/business-date/selectors'
import { NewSelectInput } from '../inputs/selects/NewSelectInput'
import ControlSection from 'app/views/pages/accounts-management/entities/components/control-section'
import { CustomLoaderPage } from 'app/views/components/loader/loader-page'
import 'app/views/components/interest-payment-schedule/interest-payment-schedule-details.css'

interface Props {
  facilityId?: string
  tab: string
}
export default function InterestPaymentScheduleDetails({
  facilityId,
  tab
}: Props) {
  const dispatch = useDispatch()
  const facilityAmendments = useSelector(getFacilityAmendments)
  const facility = useSelector(getFacility)
  const loan = useSelector(getLoan)
  const businessDate = useSelector(getBusinessDate)
  const [isLoading, setIsLoading] = useState(true)
  const [currentRows, setCurrentRows] = useState<any>([])
  const [historyRows, setHistoryRows] = useState<any>([])
  const [interestPaymentScheduler, setInterestPaymentScheduler] = useState<SchedulerList[]>([])
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [selectedStatus, setSelectedStatus] = useState('All')
  const startIdx = (activePage - 1) * itemsPerPage
  const endIdx = startIdx + itemsPerPage

  const statusOptions = ['All', 'Past Due', 'Active']

  useEffect(() => {
    if (facilityAmendments && facilityAmendments.length > 0) {
      const amendment = facilityAmendments.find(
        (amendment: any) => amendment.id === facilityId
      )
      if (amendment) {
        dispatch(setOneFacility(amendment))
      }
    }
  }, [facilityAmendments, facilityId])


  const facilityFrequency = facility?.iroValues?.find((iroValues: { indexOption: { id: string } }) => iroValues.indexOption?.id === loan?.indexOption?.id)?.interestPaymentFrequency
  const facilityFirstPaymentDate = facility?.iroValues?.find((iroValues: { indexOption: { id: string } }) => iroValues.indexOption?.id === loan?.indexOption?.id)?.interestFirstPaymentDate


  useEffect(() => {
    setIsLoading(true)
    const interestPayment = calculateInterestPaymentScheduler(
      facilityFrequency,
      loan,
      businessDate
    )
    setInterestPaymentScheduler(interestPayment as SchedulerList[])
    setIsLoading(false)
  }, [loan])


  useEffect(() => {
    setIsLoading(true)
    const filteredInterestPayment = interestPaymentScheduler
      ?.filter((scheduler: SchedulerList) =>
        selectedStatus === 'All' ||
        amortizationsStatus.find(({ label }) => label === selectedStatus)?.value === scheduler.status
      )

    const filteredCurrentRows = filteredInterestPayment
      ?.filter((scheduler: SchedulerList) => scheduler.status !== 'Paid')
      .slice(startIdx, endIdx) // Ensure pagination
      .map((scheduler: SchedulerList, index: Key | null | undefined) => (
        <tr className='interest-payment-table-row' key={index}>
          <td className='interest-payment-table-body-cell column-1'>
            <Text className='interest-payment-table-body-cell-text'>
              {formatDateToShow(new Date(scheduler?.paymentDate)).toUpperCase()}
            </Text>
          </td>
          <td className='interest-payment-table-body-cell column-2'>
            <Text
              className='interest-payment-table-body-cell-text'
              style={{
                color:
                  scheduler?.status === 'Past_Due'
                    ? '#EE5959'
                    : scheduler?.status === 'Active'
                      ? '#287A26'
                      : 'inherit',
              }}
            >
              {amortizationsStatus.find(({ value }) => value == scheduler?.status)?.label ?? ''}
            </Text>
          </td>
        </tr>
      ))

    setCurrentRows(filteredCurrentRows)

    const filteredHistoryRows = filteredInterestPayment
      ?.filter((scheduler: SchedulerList) => scheduler.status === 'Paid')
      .slice(startIdx, endIdx) // Ensure pagination
      .map((scheduler: SchedulerList, index: Key | null | undefined) => (
        <tr className='interest-payment-table-row' key={index}>
          <td className='interest-payment-table-body-cell column-1'>
            <Text className='interest-payment-table-body-cell-text'>{formatDateToShow(new Date(scheduler?.paymentDate)).toUpperCase()}</Text>
          </td>
          <td className='interest-payment-table-body-cell column-2'>
            <Text className='interest-payment-table-body-cell-text'>{scheduler?.paymentAmount ?? ''}</Text>
          </td>
        </tr>
      ))

    setHistoryRows(filteredHistoryRows)
    setIsLoading(false)
  }, [interestPaymentScheduler, selectedStatus, activePage, itemsPerPage])

  useEffect(() => {
    setActivePage(1);
  }, [tab]);

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value, 10))
    setActivePage(1)
  }

  return (
    <div className='schedule-outer-box'>
      <div className='schedule-container'>
        <div className='schedule-inner-box'>
          <div className='schedule-header'>
            <Text className='schedule-header-text'>Interest Payment Details</Text>
            <div className='schedule-header-group'>
              <div className='frequency-group'>
                <div className='frequency-label-wrapper'>
                  <Text className='frequency-label'>
                    Frequency
                  </Text>
                </div>
                <div className='interest-payment-frequency-group'>
                  <div className='interest-payment-frequency'>
                    {frequencyOptions.find(
                      option => option.value === facilityFrequency
                    )?.label ?? 'N/A'}
                  </div>
                </div>
              </div>
              <div className='first-payment-date-group'>
                <div className='first-payment-date-label-wrapper'><Text className='first-payment-date-label'>First Payment Date</Text></div>
                <div className='first-payment-date-wrapper'>
                  <div className='first-payment-date'>{facilityFirstPaymentDate ? formatDateToShow(facilityFirstPaymentDate as Date).toUpperCase() : 'N/A'}</div>
                </div>
                {tab !== 'history' && (
                  <div className='status-wrapper'>
                    <NewSelectInput
                      label='Status'
                      id='status-select'
                      className='status-select'
                      data={statusOptions}
                      onChange={(value) => {
                        setSelectedStatus(value ?? 'All')
                        setActivePage(1)
                      }}
                      defaultValue='All' />
                  </div>
                )}
              </div>
            </div>

          </div>
          <div className='interest-payment-table-wrapper'>
            {isLoading ? (<CustomLoaderPage />) : (
            <Table className='interest-payment-table' fontSize='xl' w='100%'>
              <thead className='interest-payment-table-head'>
                <tr className='interest-payment-table-row'>
                  <th className='interest-payment-table-header-cell column-1'><Text className='interest-payment-table-header-text'>Payment Date</Text></th>

                  {tab !== 'history' ? <th className='interest-payment-table-header-cell column-2'><Text className='interest-payment-table-header-text'>Status</Text></th> : (
                    <>
                      <th className='interest-payment-table-header-cell column-2'><Text className='interest-payment-table-header-text'>Payment Amount</Text></th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className='interest-payment-table-body' style={{ backgroundColor: '#fff' }}>{tab === 'current' ? currentRows : historyRows}</tbody>
            </Table>
            )}
          </div>
          <div className='interest-payment-table-control-section'>
            <ControlSection
              itemsPerPage={itemsPerPage}
              activePage={activePage}
              totalItems={Math.max(1, interestPaymentScheduler.filter(
                (scheduler: SchedulerList) =>
                  selectedStatus === 'All' ||
                  amortizationsStatus.find(({ label }) => label === selectedStatus)?.value === scheduler.status
              ).filter((scheduler: SchedulerList) => (tab === 'history' ? scheduler.status === 'Paid' : scheduler.status !== 'Paid')).length)}
              onItemsPerPageChange={handleItemsPerPageChange}
              onPageChange={setActivePage}
              onExport={() => exportToCSV(interestPaymentScheduler, 'interest-payment-schedule.csv')}
            />
          </div>
        </div>
      </div>
    </div>
  )
}